import {
    borderWidthThin,
    themeLightStateActive,
    themeLightBackgroundBase,
    themeLightContrastLow,
    themeLightPrimary,
} from '@porsche-design-system/components-react/styles';
import { isNil } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Table, { type CompanyPaginatedTableProps } from '../../components/PaginatedTableWithContext';

const StyledTable = styled(Table)`
    & a {
        color: rgb(1, 2, 5);
        text-decoration: underline;
        padding: 3px;
        border-radius: 4px;
    }

    & a:hover {
        background: ${themeLightStateActive};
    }

    &
        .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        )::before {
        display: none;
    }

    & .ant-table-tbody > tr {
        & > td {
            border-bottom-width: ${borderWidthThin};
            border-bottom-color: ${themeLightContrastLow};
        }

        &:not(:hover) > td:not(.ant-table-column-sort) {
            background-color: ${themeLightBackgroundBase};
        }
    }

    .ant-table-thead > tr > th {
        border-bottom-width: ${borderWidthThin};
        border-bottom-color: ${themeLightContrastLow};

        &:not(.ant-table-column-sort) {
            background-color: ${themeLightBackgroundBase};
        }
    }

    .ant-table-thead > tr > th {
        font-size: clamp(0.81rem, 0.23vw + 0.77rem, 0.88rem) !important;
        line-height: calc(6px + 2.125ex) !important;
        font-weight: 600 !important;

        & .ant-table-column-sorter-inner .active svg {
            color: ${themeLightPrimary};
        }
    }

    & table {
        font-size: 16px;
    }
`;

const PaginatedTableWithContext = ({ ...props }: CompanyPaginatedTableProps) => {
    const { columns, onChange } = props;
    const { t } = useTranslation('customerList');

    const sortOrder = useMemo(
        () =>
            columns.reduce(
                (acc, { key, defaultSortOrder }) => ({
                    ...acc,
                    [key as string]: defaultSortOrder || null,
                }),
                {}
            ),
        [columns]
    );

    const emptySortOrder = useMemo(
        () =>
            columns.reduce(
                (acc, { key }) => ({
                    ...acc,
                    [key as string]: null,
                }),
                {}
            ),
        [columns]
    );

    const [sorter, setSorter] = useState(sortOrder);

    const getSorterLabel = useCallback(
        sortOrder => {
            if (isNil(sortOrder)) {
                return t('customerList:sorterLabel.sortAscending');
            }

            switch (sortOrder) {
                case 'descend':
                    return t('customerList:sorterLabel.cancelSort');

                case 'ascend':
                    return t('customerList:sorterLabel.sortDescending');

                default:
                    throw new Error(`Invalid sort order: ${sortOrder}`);
            }
        },
        [t]
    );

    const enhancedOnChange = useCallback(
        (pagination, filters, sorter, extra) => {
            onChange(pagination, filters, sorter, extra);
            setSorter({ ...emptySortOrder, [sorter.columnKey]: sorter.order || null });
        },
        [emptySortOrder, onChange]
    );

    const enhancedColumns = useMemo(
        () =>
            columns.map(({ key, ...props }) => ({
                key,
                ...props,
                showSorterTooltip: {
                    title: getSorterLabel(sorter[key as string]),
                    color: 'white',
                    overlayInnerStyle: {
                        color: 'black',
                        padding: '8px 16px',
                        'font-weight': '400',
                        'font-size': '16px',
                        'border-radius': '4px',
                        filter: 'drop-shadow(rgba(0, 0, 0, 0.3) 0px 0px 16px)',
                        'backdrop-filter': 'drop-shadow(transparent 0px 0px 0px)',
                    },
                },
            })),
        [columns, getSorterLabel, sorter]
    );

    return <StyledTable {...props} bordered={false} columns={enhancedColumns} onChange={enhancedOnChange} />;
};

export default PaginatedTableWithContext;
